import { Button } from '@/src/shadComponents/ui/button';
import { Divider } from '@/src/tailwindui/divider';
import { Heading, Subheading } from '@/src/tailwindui/heading';
import { Text } from '@/src/tailwindui/text';
import { Switch } from '@/src/shadComponents/ui/switch';
import { Label } from '@/src/shadComponents/ui/label';
import { Textarea } from '@/src/tailwindui/textarea';
import ImageInput from './ImageInput';
import { cn } from '@/src/lib/utils';
import { ColorPicker } from './ColorPicker';
import Library from '@/src/Library/components/Library';
import { useState } from 'react';
import { FolderIcon } from '@heroicons/react/24/outline';

export default function ShareAndGalleryForm({
    onNext,
    foregroundColor,
    backgroundColor,
    headerForegroundColor,
    headerBackgroundColor,
    shareViaText,
    shareViaEmail,
    galleryLogo,
    setGalleryLogo,
    setShareViaText,
    setShareViaEmail,
    setCustomMessage,
    customMessage,
    setForegroundColor,
    setBackgroundColor,
    setHeaderForegroundColor,
    setHeaderBackgroundColor,
    showNextButton = false,
}: {
    onNext: () => void;
    foregroundColor: string;
    setForegroundColor: (color: string) => void;
    backgroundColor: string;
    setBackgroundColor: (color: string) => void;
    headerForegroundColor: string;
    setHeaderForegroundColor: (color: string) => void;
    headerBackgroundColor: string;
    setHeaderBackgroundColor: (color: string) => void;
    customMessage: string;
    setCustomMessage: (message: string) => void;
    shareViaText: boolean;
    setShareViaText: (enabled: boolean) => void;
    shareViaEmail: boolean;
    setShareViaEmail: (enabled: boolean) => void;
    galleryLogo: string | null;
    setGalleryLogo: (image: string | null) => void;
    showNextButton?: boolean;
}) {
    const [showLibrary, setShowLibrary] = useState(false);
    const [libraryCallback, setLibraryCallback] = useState<
        ((value: string) => void) | null
    >(null);

    return (
        <>
            <section>
                <div className="space-y-1 mb-6">
                    <Heading className="text-xl">Sharing</Heading>
                </div>
            </section>
            <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                <div className="space-y-1">
                    <Subheading>Share Methods</Subheading>
                    <Text>
                        Select the methods you want to use to share event
                        photos.
                    </Text>
                </div>
                <div>
                    <div className="flex items-center gap-2">
                        <Switch
                            aria-label="Share via Text"
                            name="share-via-text"
                            checked={shareViaText}
                            onCheckedChange={() =>
                                setShareViaText(!shareViaText)
                            }
                        />
                        <Label
                            htmlFor="share-via-text"
                            className="text-sm text-[#333333] dark:text-white"
                        >
                            SMS Messaging
                        </Label>
                    </div>
                    <br />
                    <div className="flex items-center gap-2">
                        <Switch
                            aria-label="Share via Email"
                            name="share-via-email"
                            checked={shareViaEmail}
                            onCheckedChange={() =>
                                setShareViaEmail(!shareViaEmail)
                            }
                        />
                        <Label
                            htmlFor="share-via-email"
                            className="text-sm text-[#333333] dark:text-white"
                        >
                            Email
                        </Label>
                    </div>
                </div>
            </section>

            <Divider
                className={cn(
                    'transition-all duration-300 transition-delay-200',
                    shareViaText || shareViaEmail
                        ? 'my-10 opacity-100'
                        : 'h-0 opacity-0',
                )}
                soft
            />
            <section
                className={cn(
                    'grid gap-x-8 gap-y-6 sm:grid-cols-2 transition-all duration-300 transition-delay-200',
                    shareViaText || shareViaEmail
                        ? ''
                        : 'h-0 m-0 p-0 opacity-0 overflow-hidden',
                )}
            >
                <div className="space-y-1">
                    <Subheading>Custom Message</Subheading>
                    <Text>
                        This is the body of the text message and email sent with
                        a share.
                    </Text>
                </div>
                <div>
                    <Textarea
                        aria-label="Custom Message"
                        name="custom-message"
                        className="w-full text-black dark:text-white"
                        maxLength={120}
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                    />
                    <Text className="text-right text-sm text-gray-500">
                        {120 - (customMessage || '').length} characters
                        remaining
                    </Text>
                </div>
            </section>
            <section>
                <div className="space-y-1 mb-6 mt-10">
                    <Heading className="text-xl">Gallery</Heading>
                </div>
            </section>
            <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                <div className="space-y-1">
                    <Subheading>Gallery Logo</Subheading>
                    <Text>
                        This is the logo that will be displayed in the header of
                        the gallery. For best results, use a PNG with a
                        transparent background that is 50x100 pixels.
                        <br />
                        <br />
                        <span className="text-sm text-gray-500">
                            Note: This logo will be scaled down to fit the
                            gallery.
                        </span>
                    </Text>
                </div>
                <div className="flex flex-row gap-x-2">
                    <ImageInput
                        image={galleryLogo}
                        onImageChange={setGalleryLogo}
                        key={galleryLogo}
                    />
                    <div>
                        <Button
                            className="text-sm text-[#000000] light-button-no-transparency dark:text-white plex-sans flex flex-row items-center gap-x-2  p-2 cursor-pointer"
                            onClick={() => {
                                const callback = (string: string) => {
                                    setGalleryLogo(string);
                                };
                                setLibraryCallback(() => callback);
                                setShowLibrary(true);
                            }}
                        >
                            <FolderIcon className="w-5 h-5 mr-2" />
                            Library
                        </Button>
                    </div>
                </div>
            </section>
            <Divider className="my-10" soft />
            <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                <div className="space-y-1">
                    <Subheading>Gallery Theme</Subheading>
                    <Text>
                        Choose text and background colors for the gallery.
                    </Text>
                </div>
                <div className="flex flex-col gap-4 grid grid-cols-2">
                    <Label
                        htmlFor="gallery-background-color"
                        className="text-md text-[#333333] dark:text-white"
                    >
                        Background Color
                    </Label>
                    <div className="flex items-center gap-2 mb-4">
                        <ColorPicker
                            value={backgroundColor}
                            onChange={setBackgroundColor}
                        />
                        <Text className="text-sm text-[#333333] dark:text-gray-400">
                            {backgroundColor}
                        </Text>
                    </div>

                    <Label
                        htmlFor="gallery-foreground-color"
                        className="text-md text-[#333333] dark:text-white"
                    >
                        Foreground Color
                    </Label>
                    <div className="flex items-center gap-2 mb-4">
                        <ColorPicker
                            value={foregroundColor}
                            onChange={setForegroundColor}
                        />
                        <Text className="text-sm text-[#333333] dark:text-gray-400">
                            {foregroundColor}
                        </Text>
                    </div>

                    <Label
                        htmlFor="gallery-header-color"
                        className="text-md text-[#333333] dark:text-white"
                    >
                        Header Color
                    </Label>

                    <div className="flex items-center gap-2 mb-4">
                        <ColorPicker
                            value={headerBackgroundColor}
                            onChange={setHeaderBackgroundColor}
                        />
                        <Text className="text-sm text-[#333333] dark:text-gray-400">
                            {headerBackgroundColor}
                        </Text>
                    </div>
                    <Label
                        htmlFor="gallery-header-foreground-color"
                        className="text-md text-[#333333] dark:text-white"
                    >
                        Header Foreground Color
                    </Label>
                    <div className="flex items-center gap-2 mb-4">
                        <ColorPicker
                            value={headerForegroundColor}
                            onChange={setHeaderForegroundColor}
                        />
                        <Text className="text-sm text-[#333333] dark:text-gray-400">
                            {headerForegroundColor}
                        </Text>
                    </div>
                </div>
            </section>

            <div className="my-10" />
            {showNextButton && (
                <div className="flex justify-end gap-4 select-none">
                    {/* @ts-ignore */}
                    <Button
                        className="mt-4 dark-button disabled:opacity-50 select-none font-bold"
                        onClick={() => {
                            onNext();
                        }}
                    >
                        Create Event
                    </Button>
                </div>
            )}
            {showLibrary && (
                <div
                    className="fixed inset-0 bg-black/50 bg-opacity-80 flex justify-center items-center"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowLibrary(false);
                        setLibraryCallback(null);
                    }}
                >
                    <Library
                        onImageSelected={(value: string) => {
                            console.log('value', value);
                            if (libraryCallback) {
                                libraryCallback(value);
                            }
                            setLibraryCallback(null);
                        }}
                        onClose={(e: any) => {
                            e.stopPropagation();
                            setShowLibrary(false);
                            setLibraryCallback(null);
                        }}
                    />
                </div>
            )}
        </>
    );
}
